import LineSeriesData from '@/types/line-series-data';
import { formatLocalizedString, formatMessageWithLocale } from './i18n';
import { ROLLING_INTERVALS } from './portfolios';
import RollingIntervals from '@/types/rolling-intervals';
import Locale from '@/types/locale';
import { getReturnOnInvestment } from './charts';
import LineSeriesDataPoint from '@/types/line-series-data-point';
import { ComparisonItemDAO } from '@/modules/proposal/DAOs/ProposalItemDAO';
import * as ProposalNS from '@/modules/proposal/Proposal';
import { validateAreaFillCondition } from '@/modules/proposal/ProposalHelpers';
import { currencyToText } from './currency';

/**
 * Returns the longest series of data points from an array of series.
 *
 * @param {LineSeriesDataPoint[][]} series - An array of series, where each series is an array of LineSeriesDataPoint objects.
 * @returns {LineSeriesDataPoint[]} The longest series of data points.
 */
export const getLongestGraphSerie = (series: LineSeriesDataPoint[][]) =>
  series.slice().reduce((current, serie) => (serie.length > current.length ? serie : current), series[0]);

/**
 * Generates a target rate series for a given capital and target ROI.
 *
 * @param {number} capital - The initial capital amount.
 * @param {number} targetROI - The target return on investment percentage.
 * @param {LineSeriesDataPoint[][]} series - The series data points.
 * @param {RollingIntervals} interval - The rolling interval (weekly or monthly).
 * @param {Locale} locale - The locale for formatting messages.
 * @returns {LineSeriesData} The generated target rate series data.
 */
export const generateTargetRateSeries = (capital: number, targetROI: number, series: LineSeriesDataPoint[][], interval: RollingIntervals, locale: Locale) => {
  const periodicalRate = (1 + targetROI / 100) ** (1 / (interval === ROLLING_INTERVALS.WEEKLY ? 52 : 12)) - 1;
  const longestSerie = getLongestGraphSerie(series);

  const targetRolling = longestSerie.map((period: number[]) => [period[0], periodicalRate * 100]);

  return {
    data: getReturnOnInvestment(targetRolling, capital),
    color: '#858995',
    name: formatMessageWithLocale('Charts.Growth.TargetROI', locale),
    tooltip: {
      pointFormat: '<span style="color:{point.color}">●</span> {series.name} <b style="color:#A4A7B7;">{point.y:.2f}</b><br/>',
    },
    dashStyle: 'LongDash' as const,
    lineWidth: 2,
    type: 'line' as const,
  } as LineSeriesData;
};

export const createLineSeriesEntry = (
  itemDAO: ComparisonItemDAO,
  daoConfig: ProposalNS.HypotheticalReturnConfig,
  proposal: ProposalNS.Proposal,
  series: LineSeriesDataPoint[][],
  locale: Locale,
  colorOverride: string | null,
  index: number,
): LineSeriesData => {
  const color = colorOverride === null ? itemDAO.item.color || '' : colorOverride;
  const useFillArea = daoConfig.areaFillCondition !== undefined && validateAreaFillCondition(itemDAO.item, daoConfig.areaFillCondition);
  const nameSuffix = proposal.showBaseCurrencies ? ` (${currencyToText(itemDAO.item.currency)})` : '';
  const preInceptionDateHighlight =
    // we need to be careful, some elements in the database will have a bad inception date, not testing if this is an overriden
    // item could lead to displaying dotted lines were we should not. Only a portfolio with overriden rolling data will have overrideHoldings
    daoConfig.highlightPreInceptionData != null && itemDAO.item.overrideHoldings != null ? itemDAO.item.dates.inception : null;

  return {
    data: series[index],
    color,
    name: `${formatLocalizedString(itemDAO.item.name, locale)}${nameSuffix}`,
    tooltip: {
      valueSuffix: proposal.showBaseCurrencies ? ` ${itemDAO.item.currency}` : '',
    },
    ...(preInceptionDateHighlight
      ? {
          zoneAxis: 'x',
          zones: [
            {
              value: preInceptionDateHighlight?.valueOf(),
              dashStyle: 'Dash',
            },
          ],
        }
      : {}),
    dashStyle: itemDAO.item.type === ProposalNS.ProposalItemType.Benchmark && !useFillArea ? 'Dash' : 'Solid',
    lineWidth: itemDAO.item.type === ProposalNS.ProposalItemType.Benchmark ? 1 : 2,
    fillColor: useFillArea
      ? {
          linearGradient: {
            x1: 0,
            x2: 0,
            y1: 0,
            y2: 1,
          },
          stops: [
            [0, color],
            [1, color],
          ],
        }
      : undefined,
    zIndex: useFillArea ? -1 : undefined,
  };
};
